<template>
  <article>
    <activity-stage
      :customRootContainerClass="'full-height'"
      :customContentContainerStyles="{
        height: '100%',
        ...(accessibilityMode && { height: 'calc(100% - 50px)', overflow: 'auto' })
      }"
    >
      <article slot="content">
        <div
          class="wood-pieces"
          id="activity-boundary"
          :style="{
            height: '100%',
            width: '100%',
            overflow: accessibilityMode ? 'unset' : 'auto'
          }"
        >
          <article
            :class="{ isInIframe: userDevice.isInIframe }"
            id="activity-boundary-content-container"
          >
            <div class="lettersWithWoodPieces-instructions">
              <audio-toggle-button></audio-toggle-button>
              <h1>Lets make the letter {{ currentLetter }}</h1>
            </div>
            <div class="test" id="dragging-container" style="visibility:hidden">
              <div style="position:relative;">
                <div class="woods-container" ref="woodsContainer"></div>
                <div class="exercise-woods-container" ref="exerciseWoodsContainer"></div>
              </div>
              <div class="letters-container">
                <div class="sample-container" ref="sampleContainer"></div>
                <div class="exercise-container" ref="exerciseContainer"></div>
              </div>
            </div>
          </article>
          <div
            role="region"
            aria-live="assertive"
            ref="hiddenDiv"
            style="color:transparent; height: 1px; position: absolute; top: 0; width: 100%; z-index: -1000"
          >
            {{ message }}
          </div>
        </div>
      </article>
    </activity-stage>
    <caption-reader
      class="caption-reader-fixed"
      v-if="accessibilityMode"
      ref="playerInstance"
    ></caption-reader>
  </article>
</template>

<script>
import { TweenMax, TimelineLite } from 'gsap';
import Draggable from 'gsap/Draggable';
import { mapState, mapMutations, mapActions } from 'vuex';

import ActivityStage from 'components/activity/ActivityStage.vue';
import AudioToggleButton from 'components/buttons/AudioToggleButton';
import ActivityManager from 'mixins/ActivityManager';
import TypingActivityHelper from 'mixins/TypingActivityHelper';
import ErrorLogger from 'mixins/ErrorLogger';
import PlayerManager from 'mixins/PlayerManager';

import { hasTouchedBoundings, getActivityBoundings } from 'utils/activity-util';

import littleCurve from 'assets/images/activities/wood-pieces/littleCurve.png';
import littleLine from 'assets/images/activities/wood-pieces/littleLine.png';
import bigCurve from 'assets/images/activities/wood-pieces/bigCurve.png';
import bigLine from 'assets/images/activities/wood-pieces/bigLine.png';
import smiley from 'assets/images/activities/wood-pieces/smiley.png';
import Player from 'src/components/utils/Player.vue';

export default {
  mixins: [ActivityManager, ErrorLogger, TypingActivityHelper, PlayerManager],
  components: {
    ActivityStage,
    'caption-reader': Player,
    AudioToggleButton
  },
  data() {
    return {
      activityName: 'WoodPieces',
      ctHeight: 360,
      ctWidth: 270,
      currentLetter: '',
      currentShape: 0,
      message: '',
      sfx: [],
      soundsBucket: {},
      captionsBucket: {},
      draggableInstances: [],
      selectedStrokeNr: null,
      selectedStroke: null
    };
  },

  computed: {
    ...mapState('application', {
      isLiteVersion: state => state.isLiteVersion,
      accessibilityMode: state => state.accessibilityMode
    }),
    ...mapState('accessibility', {
      soundMuted: state => state.soundMuted,
      soundVolume: state => state.soundVolume
    })
  },

  mounted() {
    this._resizeTimeout = null;
    window.addEventListener('resize', this.handleWindowResizeDebounced);
  },
  beforeDestroy() {
    window.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('resize', this.handleWindowResizeDebounced);
    if (this._resizeTimeout) clearTimeout(this._resizeTimeout);
    TweenMax.killAll();
    Howler.volume(1); // eslint-disable-line no-undef
  },
  methods: {
    ...mapActions('activity', ['restartActivity']),
    ...mapMutations('accessibility', [
      'setOnGoingAnimation',
      'setCurrentTweenData',
      'setAnimationsMode'
    ]),
    async loadActivity() {
      this.createRounds();
      this.sfx = await this.loadAssets();

      this.soundsBucket = this.sfx[1];
      this.captionsBucket = this.sfx[2];
    },

    async loadImages() {
      this.queueImage(littleLine);
      this.queueImage(bigLine);
      this.queueImage(littleCurve);
      this.queueImage(bigCurve);
      this.queueImage(smiley);
      this.downloadImages();
    },

    async loadSounds() {
      const stepAudioPaths = [];
      const lineAudioPaths = {};
      const general = {};

      this.rounds.forEach(round => {
        round.steps.forEach(step => {
          Object.assign(lineAudioPaths, { [step.stroke]: step.strokeAudio.src });

          this.queueAudio(lineAudioPaths[step.stroke]);
        });
      });

      this.rounds.forEach(
        ({ completedAudio, instructionAudio, introductionAudio, affirmationAudio }) => {
          this.queueAudio(instructionAudio.src);
          this.queueAudio(completedAudio.src);
          this.queueAudio(introductionAudio.src);
          this.queueAudio(affirmationAudio.src);
          stepAudioPaths.push({
            instructionAudio,
            completedAudio
          });
          Object.assign(general, {
            generalCongrats: affirmationAudio.src,
            nowitsyourturn: introductionAudio.src
          });
        }
      );

      const cache = await this.downloadAudio();

      return {
        soundsPerStep: stepAudioPaths.map(audioPaths => ({
          instructionAudio: cache.get(audioPaths.instructionAudio.src),
          completedAudio: cache.get(audioPaths.completedAudio.src)
        })),
        lineSounds: Object.keys(lineAudioPaths).map(key => ({
          [key]: cache.get(lineAudioPaths[key])
        })),
        general: {
          generalCongrats: cache.get(general.generalCongrats),
          nowitsyourturn: cache.get(general.nowitsyourturn)
        }
      };
    },

    async loadCaptions() {
      const stepTextPaths = [];
      const lineTextPaths = {};
      const general = {};

      this.rounds.forEach(
        ({ completedAudio, instructionAudio, introductionAudio, affirmationAudio, steps }) => {
          stepTextPaths.push({
            instructionAudio: instructionAudio.captions.cues,
            completedAudio: completedAudio.captions.cues
          });
          Object.assign(general, {
            nowitsyourturn: introductionAudio.captions.cues,
            generalCongrats: affirmationAudio.captions.cues
          });

          steps.forEach(step => {
            Object.assign(lineTextPaths, { [step.stroke]: step.strokeAudio.captions.cues });
          });
        }
      );

      return {
        stepTextPaths,
        lineTextPaths,
        general
      };
    },
    handleWindowResizeDebounced() {
      if (this._resizeTimeout) clearTimeout(this._resizeTimeout);
      this._resizeTimeout = setTimeout(() => {
        this.restartActivityAfterResize();
      }, 400);
    },
    restartActivityAfterResize() {
      this.setActivityContainerHeight();
      this.$nextTick(() => {
        this.adjustContainers();
        this.restartActivity(false);
      });
    },

    playSound(sound, caption, accessibilityMode) {
      if (accessibilityMode) {
        const { playerInstance } = this.$refs;
        playerInstance.playSoundAndShowCaptions(sound, caption);
        return;
      }
      sound.play();
    },
    playStepInstructions() {
      this.currentLetter = this.currentRound.letter;

      const { soundsBucket, captionsBucket, accessibilityMode } = this;

      const currentSound = soundsBucket.soundsPerStep[this.progress.roundIndex].instructionAudio;
      const currentCaption =
        captionsBucket.stepTextPaths[this.progress.roundIndex].instructionAudio;

      this.playSound(currentSound, currentCaption, accessibilityMode);

      this.$nextTick(() => {
        this.setInstructionsAudio(currentSound);
      });
    },
    playYourTurn() {
      const { soundsBucket, captionsBucket, accessibilityMode } = this;

      this.playSound(
        soundsBucket.general.nowitsyourturn,
        captionsBucket.general.nowitsyourturn,
        accessibilityMode
      );
    },
    playStepCongrats() {
      const { soundsBucket, captionsBucket, accessibilityMode } = this;
      const stepSound = soundsBucket.soundsPerStep[this.progress.roundIndex].completedAudio;
      const stepCaption = captionsBucket.stepTextPaths[this.progress.roundIndex].completedAudio;
      this.playSound(stepSound, stepCaption, accessibilityMode);
    },

    async loadAssets() {
      const resolutions = await Promise.all([
        this.loadImages(),
        this.loadSounds(),
        this.loadCaptions()
      ]);

      return resolutions;
    },

    startActivity() {
      this.setupNewRound();
      this.errorLoggerStart();

      this.$nextTick(() => {
        this.startDemo();
        this.$nextTick(this.setActivityContainerHeight);
      });
    },

    startDemo() {
      const self = this;
      // play instruction sound for current letter
      this.playStepInstructions();

      TweenMax.delayedCall(2.5, () => {
        self.adjustContainers();
        TweenMax.to('.test', 2, { css: { autoAlpha: 1 } });
        TweenMax.set('.sample-container', { css: { x: 150, autoAlpha: 1 } });
        TweenMax.set('.exercise-container', { css: { autoAlpha: 0 } });
        self.injectDemoShapes();
        self.setSmiley('sample-container');
        self.setSmiley('exercise-container');
      });
    },

    handleAriaLive(text) {
      this.message = text;
      setTimeout(() => {
        this.message = ' ';
      }, 500);
    },

    drawDroppableArea(shape, i, img, theLabel) {
      const getLabel = this.getLabel(shape.stroke);

      const droppableCt = document.createElement('button');
      droppableCt.setAttribute('id', `droppableContainer-${i}`);
      droppableCt.setAttribute('class', 'droppableContainer');
      droppableCt.setAttribute('aria-label', theLabel(getLabel));

      Object.assign(droppableCt.style, {
        position: 'absolute',
        transform: `rotate(${shape?.stepsPosition?.r}deg)`,
        width: `${img.naturalWidth * this.scaleParam}px`,
        height: `${img.naturalHeight * this.scaleParam}px`,
        left: `${shape.stepsPosition.x * this.scaleParam}px`,
        top: `${shape.stepsPosition.y * this.scaleParam + 20}px`,
        transformOrigin: '0% 0%',
        background: 'transparent',
        border: 'none'
      });

      droppableCt.addEventListener('click', this.handleDropWithKeyboard);

      return droppableCt;
    },

    scaleContainers() {
      const bodyHeight = document.body.offsetHeight;
      const desktopHeight = 960;
      return bodyHeight <= 700 ? bodyHeight / desktopHeight : 0.9;
    },

    /**
     * due to the way webpack works (only dist folder is used in published version of the app)
     * we import the local images, instead of giving the relative path, and use the
     * generated link "/dist/localImageName.extension?generated code"
     * also we create a separate div for smiley background image so it will not conflict
     * with the gradient background color of the container
     * */
    setSmiley(containerClass) {
      const url = `url(${smiley})`;
      const containerDiv = document.getElementsByClassName(containerClass)[0];
      const smileyCt = document.createElement('div');
      smileyCt.style.width = '18px';
      smileyCt.style.height = '18px';
      smileyCt.style.margin = '5px';
      smileyCt.style.backgroundImage = url;
      smileyCt.setAttribute('aria-label', 'Smiley face');
      smileyCt.setAttribute('role', 'img');
      smileyCt.setAttribute('tabindex', '-1');
      containerDiv.appendChild(smileyCt);
    },

    adjustContainers() {
      const isSmallScaleContainer = this.scaleContainers() < 1;
      const scaleParam = Math.round(this.scaleContainers() * 100) / 100;

      TweenMax.set('.sample-container', {
        css: {
          height: this.ctHeight * scaleParam + (isSmallScaleContainer ? 20 : 0),
          width: this.ctWidth * scaleParam
        }
      });
      TweenMax.set('.exercise-container', {
        css: {
          height: this.ctHeight * scaleParam + (isSmallScaleContainer ? 20 : 0),
          width: this.ctWidth * scaleParam
        }
      });
    },

    // we create wood images, non draggable, for Demo animation
    injectDemoShapes() {
      const self = this;
      this.currentRound.steps.forEach((shape, i) => {
        const shapeCt = document.createElement('div');
        shapeCt.style.display = 'inline-block';
        shapeCt.style.zIndex = 2;
        shapeCt.setAttribute('id', self.getShapeCtDemoId(i));
        shapeCt.setAttribute('blockType', shape.stroke.toLowerCase());

        const img = self.createImage(shape, i, true);
        img.setAttribute('alt', `${shape.stroke} image`);

        TweenMax.set(img, { css: { autoAlpha: 0 } });
        shapeCt.appendChild(img);
        self.$refs.woodsContainer.appendChild(shapeCt);
      });
      TweenMax.delayedCall(0.2, () => {
        self.fitDemoImagesToCt();
      });
    },
    fitDemoImagesToCt() {
      const self = this;

      const tlInject = new TimelineLite({
        onComplete: self.moveShapesToDemoContainer,
        onCompleteParams: ['sample-container']
      });

      const shapes = self.$refs.woodsContainer.children;
      const shapesArray = [].slice.call(shapes);

      shapesArray.forEach(shapeCt => {
        const img = shapeCt.children[0];
        self.fitToCt(shapeCt, img, true);
        tlInject.set(img, {
          css: { autoAlpha: 1 },
          onStart() {}
        });
      });
    },

    getLabel(txt) {
      return txt.split(/(?=[A-Z])/).join(' ');
    },

    getDuplicateElement(arr) {
      let multiple = [];
      if (arr.length) {
        multiple = arr
          .map(e => e.stroke)
          .map((e, i, final) => final.indexOf(e) !== i && i)
          .filter(obj => arr[obj])
          .map(e => arr[e].stroke);
      }
      return multiple.length && multiple[0];
    },

    setLabel(preText, label, postText) {
      const postTextVal = `${postText ? `. ${postText}` : ''}`;

      let duplicatedLabel;
      if (label) duplicatedLabel = this.getLabel(label);

      let count = 0;
      const order = {
        1: 'first',
        2: 'second',
        3: 'third',
        4: 'fourth'
      };

      return receivedLabel => {
        // set correct aria-label based on number of same elements with the same labels
        // eg: first big line, second big line etc.
        if (duplicatedLabel === receivedLabel) {
          count += 1;
          return `${preText} ${order[count]} ${receivedLabel}${postTextVal}`;
        }
        return `${preText} ${receivedLabel}${postTextVal}`;
      };
    },

    // we create wood images, draggable, for exercise
    injectExerciseShapes() {
      const self = this;
      const { isMobile } = this.userDevice;
      const tLite = new TimelineLite();
      const parentContainer = isMobile
        ? document.getElementById('dragging-container')
        : document.getElementById('activity-boundary');
      let hitBounds = null;
      const duplicatedElement = this.getDuplicateElement(this.currentRound.steps);

      const theLabel = this.setLabel('Wood Piece for', duplicatedElement);
      const theDropabbleLabel = this.setLabel('Droppable area for', duplicatedElement);

      this.currentRound.steps.forEach((shape, i) => {
        let dragTimer = 0;
        let hasHitBoundary = false;
        this.scaleParam = Math.round(this.scaleContainers() * 100) / 100;
        const img = self.createImage(shape, i, false);

        const getLabel = this.getLabel(shape.stroke);

        const shapeCt = document.createElement('div');
        shapeCt.style.display = 'inline-block';
        shapeCt.style.zIndex = 2;
        shapeCt.setAttribute('id', self.getShapeCtExerciseId(i));
        const button = document.createElement('button');
        button.setAttribute('tabindex', '0');
        button.setAttribute('aria-pressed', false);
        button.setAttribute('aria-label', theLabel(getLabel));

        const buttonId = this.getExerciseImageId(i);
        button.setAttribute('id', buttonId);

        Object.assign(button.style, {
          backgroundImage: `url(${shape.strokeImage.src})`,
          backgroundColor: 'transparent',
          backgroundSize: 'cover',
          border: 'none',
          transformOrigin: '0% 0%',
          transform: `rotate(${shape?.stepsPosition?.r}deg)`
        });

        TweenMax.delayedCall(0.1, () => {
          Object.assign(button.style, {
            width: `${img.naturalWidth * this.scaleParam}px`,
            height: `${img.naturalHeight * this.scaleParam}px`
          });
          const droppableCt = this.drawDroppableArea(shape, i, img, theDropabbleLabel);
          TweenMax.set(button, { css: { autoAlpha: 0 } });
          shapeCt.appendChild(button);
          // play ding sound
          self.$refs.exerciseWoodsContainer.appendChild(shapeCt);
          self.$refs.exerciseContainer.appendChild(droppableCt);

          // make selected shape draggable
          this.draggableInstances.push(
            Draggable.create(button, {
              autoScroll: 1,
              cursor: 'grab',
              type: 'x, y',
              shapeName: shape.stroke,
              bounds: parentContainer,
              edgeResistance: this.isMobile ? 0.0 : 1.1,
              onPress(param) {
                if (!param.pressure && !self.isMobile) {
                  self.selectStroke(param, true);
                  return;
                }

                const button = document.getElementById(buttonId);
                const siblingButtonDiv = button?.nextSibling;

                if (siblingButtonDiv) siblingButtonDiv.style.display = 'none';
                if (hitBounds || !document.querySelector('.activity-wrapper')) return;

                hitBounds = getActivityBoundings();
              },
              onDrag() {
                // work-around for iOS not firing touchend on tablet
                if (!isMobile) return;
                clearTimeout(dragTimer);
                const { pointerX, pointerY } = this;
                const hasTouched = hasTouchedBoundings(pointerX, pointerY, hitBounds);
                if (hasTouched && !hasHitBoundary) {
                  dragTimer = setTimeout(() => {
                    hasHitBoundary = true;
                    self.handleDrop(this, this.target);
                  }, 200);
                }
              },
              onDragStart() {
                tLite.set(button.style, { cursor: 'grabbing' });
              },
              onDragEnd() {
                clearTimeout(dragTimer);
                hasHitBoundary = false;
                self.handleDrop(this);
              }
            })
          );
        });

        button.addEventListener('keydown', this.selectStroke);
      });

      // we need this delay so dom elements have proper new coordinates after si
      // injection and rendering
      TweenMax.delayedCall(0.2, () => {
        self.fitExerciseImagesToCt();
      });
      // we delay the display, to wait for fitToCt() on each image to fulfill
      TweenMax.delayedCall(1, () => {
        self.displayExerciseShapes(0);
      });
    },
    fitExerciseImagesToCt() {
      const self = this;

      const shapes = self.$refs.exerciseWoodsContainer.children;
      const shapesArray = [].slice.call(shapes);

      shapesArray.forEach(shapeCt => {
        const img = shapeCt.children[0];
        self.fitToCt(shapeCt, img, false);
      });
    },
    createImage(shape, i, isDemo) {
      const img = new Image();
      const scaleParam = Math.round(this.scaleContainers() * 100) / 100;

      img.src = this.getImagePath(shape.stroke);
      if (isDemo) {
        img.setAttribute('id', this.getDemoImageId(i));
        img.draggable = false;
      } else {
        img.setAttribute('id', this.getExerciseImageId(i));
      }

      img.style.transformOrigin = '0% 0%';
      img.style.transform = `rotate(${shape?.stepsPosition?.r}deg)`;

      img.style.height = '100%';

      TweenMax.delayedCall(0.1, () => {
        img.style.width = `${img.naturalWidth * scaleParam}px`;
        img.style.height = `${img.naturalHeight * scaleParam}px`;
      });

      return img;
    },
    getImagePath(blockType) {
      switch (blockType) {
        case 'BigLine':
          return bigLine;
        case 'LittleLine':
          return littleLine;
        case 'BigCurve':
          return bigCurve;
        case 'LittleCurve':
          return littleCurve;
        default:
          return '';
      }
    },

    /**
     * for exercise, we make shapes visible one by one, joind by a ding sound
     * attention: recursive method
     * */
    displayExerciseShapes(currentIndex) {
      const self = this;
      const currentImg = document.getElementById(this.getExerciseImageId(currentIndex));

      // if no more shapes => stop recursivity
      if (!currentImg) {
        self.handleAriaLive(
          `Select the wood pieces with the ENTER or SPACE key, then drop them in their corresponding droppable area using ENTER or SPACE again. Place the wood pieces in the sequence they appear to form capital letter ${self.currentLetter}.`
        );
        return;
      }

      Howler.volume(this.soundMuted ? 0 : this.soundVolume); // eslint-disable-line no-undef
      self.sndBell();
      TweenMax.set(currentImg, { css: { autoAlpha: 1 } });

      const label = currentImg.getAttribute('aria-label') + ' added';
      self.handleAriaLive(label);
      // eslint-disable-next-line
      const nextIndex = (currentIndex += 1);
      // display next exercise woodshape and play a spound, with a delay of one second
      // using Timeline to display each shape and play a bell is not working using Timeline,
      // so I will use delayedCall
      // [INFO] Raluca : http://greensock.com/forums/topic/3413-onstart-onstartparams/
      TweenMax.delayedCall(1.2, () => {
        self.displayExerciseShapes(nextIndex);
      });
    },

    /**
     * when the image is created and injected in DOM,
     * after ROTATION around the top-left corner, the image will go out of the container
     * where initially was placed, so we move it back to shapeCt
     * */
    fitToCt(shapeCt, img) {
      const divEl = shapeCt;
      const imgEl = document.getElementById(img.id);

      if (imgEl.getBoundingClientRect().left < divEl.getBoundingClientRect().left) {
        imgEl.setAttribute(
          'fitOffsetX',
          divEl.getBoundingClientRect().left - imgEl.getBoundingClientRect().left
        );
        TweenMax.set(imgEl, {
          css: {
            x: divEl.getBoundingClientRect().left - imgEl.getBoundingClientRect().left
          }
        });
      } else {
        imgEl.setAttribute('fitOffsetX', 0);
      }

      if (imgEl.getBoundingClientRect().top < divEl.getBoundingClientRect().top) {
        imgEl.setAttribute(
          'fitOffsetY',
          divEl.getBoundingClientRect().top - imgEl.getBoundingClientRect().top
        );
        TweenMax.set(imgEl, {
          css: {
            y: divEl.getBoundingClientRect().top - imgEl.getBoundingClientRect().top
          }
        });
      } else {
        imgEl.setAttribute('fitOffsetY', 0);
      }
    },

    /**
     * at Demo, we take injected demo shapes (the one created in injectDemoShapes())
     * and animate them to Demo container
     * */
    moveShapesToDemoContainer(targetContainerClass) {
      const self = this;
      const { soundsBucket, captionsBucket, accessibilityMode } = this;
      const scaleParam = Math.round(this.scaleContainers() * 100) / 100;
      const margins = {
        top: scaleParam < 1 ? 20 : 0
      };

      // move shapes to sample container
      const targetCoords = self.getTargetCoordinates(targetContainerClass);
      const tl = new TimelineLite({
        onComplete() {
          self.moveCtsLeft();
          self.playYourTurn();
        }
      });

      this?.currentRound?.steps.forEach(({ stroke, stepsPosition }, stepIndex) => {
        const wood = document.getElementById(this.getDemoImageId(stepIndex));
        const sourceCoords = self.getPositionForElement(document.getElementById(wood.id));
        const scaledX = stepsPosition.x * scaleParam;
        const scaledY = stepsPosition.y * scaleParam;
        const translateCoords = {
          x: targetCoords.left - sourceCoords.left,
          y: targetCoords.top - sourceCoords.top
        };

        const tween = tl.to(wood, 2, {
          css: {
            x: translateCoords.x + scaledX,
            y: translateCoords.y + scaledY + margins.top
          },
          delay: 0.1,
          onStart() {
            self.setCurrentTweenData(stepIndex + 1);
            const strokeSound = soundsBucket.lineSounds.find(sound => sound[stroke]);
            const strokeCaption = captionsBucket.lineTextPaths[stroke];

            self.playSound(strokeSound[stroke], strokeCaption, accessibilityMode);
          }
        });
        this.setOnGoingAnimation(tween);
      });
      this.setCurrentTweenData(1);
    },

    /**
     * when the user puts the shape in the correct position, we set the next current shape
     * if it was the last shape from letter, we move to next letter
     * if it was the last letter, we complete activity
     * */
    nextShape() {
      const self = this;
      this.currentShape += 1;
      if (this.currentShape === this.currentRound.steps.length) {
        // play congratulations sound for current letter
        this.playStepCongrats();
        this.draggableInstances = [];
        if (this.currentRound.roundIndex < this.rounds.length - 1) {
          this.nextLetter();
        } else {
          // we deplay call, to wait for end round congratulations to finish
          this.errorLoggerStop();
          TweenMax.delayedCall(2.5, () => {
            self.winGame();
          });
        }
      }
    },

    /**
     * when the user finish to build one letter, we change the UI
     * (containers, wood shapes) for next letter,
     * and start the Demo for next letter
     * */
    nextLetter() {
      const self = this;

      TweenMax.to('.activity-stage', 2.5, {
        css: { autoAlpha: 0 },
        onComplete() {
          self.setupNewRound();

          self.currentShape = 0;
          self.$refs.exerciseWoodsContainer.innerHTML = '';
          self.$refs.woodsContainer.innerHTML = '';
          self.$refs.sampleContainer.innerHTML = '';
          self.$refs.exerciseContainer.innerHTML = '';

          self.startDemo();
        }
      });
    },

    /**
     * move demo container to the left after demo animation is finished
     * */
    moveCtsLeft() {
      const self = this;
      const tl = new TimelineLite({
        onComplete() {
          self.injectExerciseShapes();
        }
      });
      this.setOnGoingAnimation(tl);
      TweenMax.to('.sample-container', 1, { css: { x: 0 } });
      TweenMax.staggerTo(this.$refs.woodsContainer.children, 1, { css: { x: -150 } }, '-=1');
      tl.to('.exercise-container', 1, { css: { autoAlpha: 1 } });
      this.setOnGoingAnimation(null);
      this.setAnimationsMode(true);
    },

    getPositionForElement(el) {
      return {
        left: el.getBoundingClientRect().left,
        top: el.getBoundingClientRect().top
      };
    },

    getTargetCoordinates(className) {
      const el = document.querySelector(`.${className}`);
      return {
        left: el.getBoundingClientRect().left,
        top: el.getBoundingClientRect().top
      };
    },

    handleDrop(dragContext) {
      // check if it's the right currentShape
      if (this.isCurrentShape(dragContext.target) && this.isInCorrectPosition(dragContext.target)) {
        dragContext.kill();
        Howler.volume(this.soundMuted ? 0 : this.soundVolume); // eslint-disable-line no-undef
        this.sndBell();
        this.handleAriaLive('Good Job!');
        this.moveShapeToFinalPosition(dragContext.target);
        this.nextShape();
      } else {
        this.sndError();
        this.handleAriaLive(
          `${dragContext?.target?.getAttribute(
            'aria-label'
          )} doesn't go there. Try a different option.`
        );
        this.logError(dragContext.vars.shapeName, ' ', 'DNDplace');
        const fitOffsetX = dragContext.target.attributes.fitOffsetX.value;
        const fitOffsetY = dragContext.target.attributes.fitOffsetY.value;
        TweenMax.to(dragContext.target, 0.5, { css: { x: fitOffsetX, y: fitOffsetY } });
      }
    },
    /**
     * when wood was dropped in an approximately correct position,
     * we move(animate) it into EXACT correct position in Exercise container
     * */
    moveShapeToFinalPosition(target) {
      const wood = document.getElementById(this.getExerciseImageId(this.currentShape));
      const initialPosition = this.getInitialPosition();
      const finalPosition = this.getFinalPosition();
      const currentPosition = this.getCurrentPosition(target);

      const translateCoordsTo = {
        x: finalPosition.left - initialPosition.left,
        y: finalPosition.top - initialPosition.top
      };
      const translateCoordsFrom = {
        x: currentPosition.left - initialPosition.left,
        y: currentPosition.top - initialPosition.top
      };

      TweenMax.fromTo(
        wood,
        0.15,
        { css: { x: translateCoordsFrom.x, y: translateCoordsFrom.y } },
        { css: { x: translateCoordsTo.x, y: translateCoordsTo.y } }
      );
    },

    isCurrentShape(target) {
      return target.id === this.getExerciseImageId(this.currentShape);
    },

    isInCorrectPosition(target) {
      const finalPosition = this.getFinalPosition();
      const currentPos = this.getCurrentPosition(target);
      const translateCoords = {
        x: currentPos.left - finalPosition.left,
        y: currentPos.top - finalPosition.top
      };
      return (
        translateCoords.x < 50 &&
        translateCoords.x > -50 &&
        translateCoords.y < 50 &&
        translateCoords.y > -50
      );
    },

    getInitialPosition() {
      const currentShapeCtId = this.getShapeCtExerciseId(this.currentShape);
      const currentShape = document.getElementById(currentShapeCtId);
      return {
        left: currentShape.getBoundingClientRect().left,
        top: currentShape.getBoundingClientRect().top
      };
    },
    getCurrentPosition(target) {
      return {
        left:
          target.getBoundingClientRect().left + parseInt(target.attributes.fitOffsetX.value, 10),
        top: target.getBoundingClientRect().top + parseInt(target.attributes.fitOffsetY.value, 10)
      };
    },

    getFinalPosition() {
      const scaleParam = Math.round(this.scaleContainers() * 100) / 100;
      const margins = {
        top: scaleParam < 1 ? 20 : 0
      };
      const shape = this.currentRound.steps[this.currentShape];
      const ctCoords = this.getTargetCoordinates('exercise-container');
      const scaledX = shape.stepsPosition.x * scaleParam;
      const scaledY = shape.stepsPosition.y * scaleParam;
      return {
        left: ctCoords.left + scaledX,
        top: ctCoords.top + scaledY + margins.top
      };
    },

    winGame() {
      const self = this;
      // fade out all containers
      TweenMax.staggerTo(
        [
          '.sample-container',
          '.exercise-container',
          '.woods-container',
          '.exercise-woods-container'
        ],
        1,
        { css: { autoAlpha: 0 } }
      );
      TweenMax.delayedCall(2, () => {
        self.completeActivity();
      });
    },

    playCongrats() {
      const { soundsBucket, captionsBucket, accessibilityMode } = this;
      this.playSound(
        soundsBucket.general.generalCongrats,
        captionsBucket.general.generalCongrats,
        accessibilityMode
      );
    },

    blurElement(element) {
      element.blur();
      element.setAttribute('tabindex', '-1');
      element.setAttribute('aria-pressed', false);
      element.classList.remove('highlight');
    },

    isSelected(index) {
      const button = this.$refs.exerciseWoodsContainer.children[index].children[0];
      return button.getAttribute('aria-pressed') === 'true';
    },
    toggleSelected(id) {
      const index = id.split('-')[1];
      const pressed = this.isSelected(index);
      const stroke = this.$refs.exerciseWoodsContainer.children[index].children[0];
      if (!pressed) {
        stroke.classList.add('highlight');
      } else {
        stroke.classList.remove('highlight');
      }
      this.handleHighlightDroppables(!pressed);
      stroke.setAttribute('aria-pressed', !pressed);
    },
    handleHighlightDroppables(highLight) {
      const droppableCts = Array.from(this.$refs.exerciseContainer.children);
      const availableSpots = droppableCts.filter(ct => ct.getAttribute('tabIndex') !== '-1');

      Array.from(availableSpots).forEach(spot => {
        highLight ? spot.classList.add('highlight') : spot.classList.remove('highlight');
        spot.setAttribute('tabindex', '0');
      });
    },
    selectStroke(event, isPointerEvent) {
      const shapeId = event.target.id;
      const index = shapeId.split('-')[1];
      if (event.key === 'Enter' || event.key === ' ' || isPointerEvent) {
        this.toggleSelected(shapeId);
        if (this.isSelected(index)) {
          // eslint-disable-next-line prefer-destructuring
          this.selectedStrokeNr = shapeId.split('-')[1];
          if (this.selectedStroke) {
            this.selectedStroke.setAttribute('aria-pressed', false);
            this.selectedStroke.classList.remove('highlight');
          }
          this.selectedStroke = document.getElementById(shapeId);
        } else {
          this.selectedStrokeNr = null;
          this.selectedStroke = null;
        }
      }
    },
    handleDropWithKeyboard(event) {
      const droppableSlotNr = event.target.id.split('-')[1];
      if (this.selectedStroke) {
        if (this.isCurrentShape(this.selectedStroke) && droppableSlotNr === this.selectedStrokeNr) {
          this.handleHighlightDroppables(false);
          this.moveShapeToFinalPosition(this.selectedStroke);
          this.sndBell();
          this.handleAriaLive('Good job!');
          this.blurElement(this.selectedStroke);
          this.blurElement(event.target);
          this.nextShape();
        } else {
          this.sndError();
          this.handleAriaLive(
            `${this.selectedStroke?.getAttribute(
              'aria-label'
            )} doesn't go there. Try a different option.`
          );
        }
      }
    },

    getDemoImageId(i) {
      return `imgDemo${i}`;
    },

    getExerciseImageId(i) {
      return `imgExercise-${i}`;
    },

    getShapeCtDemoId(i) {
      return `shapeCtDemo${i}`;
    },

    getShapeCtExerciseId(i) {
      return `shapeCtExercise${i}`;
    }
  }
};
</script>

<style lang="scss" scoped>
.is-chrome-ipad {
  #dragging-container {
    width: 99%;
    height: 99%;
  }

  .letters-container {
    > div {
      height: 250px;
    }
  }
}

.lettersWithWoodPieces-instructions {
  font-size: 18px;
  margin-top: 5px;
  margin-bottom: 3px;
  position: relative;

  button {
    position: absolute;
  }

  h1 {
    font-family: hwtfutura;
    font-size: 18px;
    left: 32px;
    margin: 0px 0px 0px 10px;
    position: relative;
    top: 10px;
    width: calc(100% - 50px);
    -webkit-font-smoothing: auto;
  }
}

#activity-boundary-content-container {
  &:not(.isInIframe) {
    @media (max-width: 863px) {
      width: 1024px;
    }
  }
}
.caption-reader-fixed {
  position: absolute;
  right: 5px;
  bottom: 20px;
  width: 100%;
  max-width: 100%;
}
</style>
